import { useCallback, useMemo } from 'react';
import { Flex } from '@kognitos/lattice';

import InlineGuideEntryWidget from '@/modules/automation-run/components/run-assistant/InlineGuideEntryWidget';
import GuideEntryApprovalPills from '@/modules/automation-run/components/run-assistant/GuideEntryApprovalPills';
import { useGuideEntryTransition } from '@/modules/automation-run/hooks/useGuideEntryTransition';
import type { IParsedGuideEntry } from '@/shared/utils/chat-utils';

/**
 * Props for {@link RainGuideApprovalCard}.
 */
export interface IRainGuideApprovalCardProps {
  /**
   * Guide-entry resource name carried by the `request_guide_approval` tool
   * (`organizations/.../automations/.../guideEntries/{id}`). The widget and
   * pills fetch the entry by this name to render and reflect its live state.
   */
  guideEntryName: string;
  /**
   * Resolves the remote tool call with the user's decision (`true` = approve,
   * `false` = reject) so the blocked agent turn can continue. Returns true once
   * the tool_result was submitted.
   */
  onResolve: (approved: boolean) => Promise<boolean>;
  /**
   * Invoked when the user clicks "Edit in chat" — the caller focuses the
   * composer so the user can type refinements to the proposed guidance.
   */
  onEditInChat?: () => void;
  /** Whether the tool result has already been submitted (card is settling). */
  isSubmitting?: boolean;
}

/**
 * Renders the guide entry proposed by the astral2 `request_guide_approval` remote
 * tool. The presentation (widget) and the state-driven actions (pending
 * "Add to Resolution Playbook" / "Reject" pills, or the "View in Resolution
 * Playbook" pill once approved) are the same {@link InlineGuideEntryWidget} and
 * {@link GuideEntryApprovalPills} the astral flow uses — this card only wires the
 * decision so it ALSO resolves the blocking tool call:
 *   1. Transitions the guide entry's state (APPROVE/REJECT).
 *   2. Resolves the remote tool call via {@link onResolve} so the agent turn,
 *      which is blocked waiting on this tool, unblocks.
 */
export function RainGuideApprovalCard({
  guideEntryName,
  onResolve,
  onEditInChat,
  isSubmitting,
}: IRainGuideApprovalCardProps) {
  const { approveGuideEntry, rejectGuideEntry, isTransitioning } =
    useGuideEntryTransition();

  // The widget/pills fetch the entry by `name`; the other fields are placeholders
  // satisfying the type until the fetch resolves. `action: 'create'` matches the
  // tool's intent (the agent is proposing a new entry).
  const guideEntry: IParsedGuideEntry = useMemo(
    () => ({
      action: 'create',
      name: guideEntryName,
      title: '',
      rootCause: '',
      resolutionSteps: '',
      state: '',
      version: '',
    }),
    [guideEntryName],
  );

  const handleApprove = useCallback(async () => {
    await approveGuideEntry(guideEntry);
    await onResolve(true);
  }, [approveGuideEntry, guideEntry, onResolve]);

  const handleReject = useCallback(async () => {
    await rejectGuideEntry(guideEntry);
    await onResolve(false);
  }, [rejectGuideEntry, guideEntry, onResolve]);

  return (
    <Flex direction="column" gap={3} className="w-full">
      <InlineGuideEntryWidget guideEntry={guideEntry} />
      <GuideEntryApprovalPills
        guideEntry={guideEntry}
        onApprove={handleApprove}
        onReject={handleReject}
        onEdit={onEditInChat}
        isLoading={isTransitioning || isSubmitting}
      />
    </Flex>
  );
}
